import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { AppDispatch } from "../../../../redux/store";
import {
  clearDeleteReasonState,
  clearNewReasonState,
  clearUpdateReasonState,
  deleteReasonStateAction,
  getCreateReasonState,
  getDeleteReasonState,
  getReasonsState,
  getUpdateReasonState,
  ReasonResponse,
  reasonsStateAction,
} from "../../../../redux/features/other-slice";
import {
  getErrorState,
  setBreadcrumbState,
} from "../../../../redux/features/common-slice";
import paths from "../../../../navigation/paths";
import PermissionGate from "../../../../core/layouts/permission-gate";
import { UserRole } from "../../../../redux/features/user-slice";
import Table from "../../../../components/table";
import Loading from "../../../../components/loading";
import TableBodyCell from "../../../../components/table-body-cell";
import Button from "../../../../components/button";
import ToastComponent from "../../../../components/toast";
import UpdateReasonDialog from "./components/update-reason-dialog";
import { adminCat, mgTeam } from "../../../../core/utils/constants";
import NewReasonDialog from "./components/new-reason-dialog";
import EmptyDataOverlay from "../../../../components/empty-data-overlay";
import PropertyActions from "../_components/property-actions";

export default function ReasonsPage() {
  const navigate = useNavigate();

  const { id, name } = useParams();

  const dispatch = useDispatch<AppDispatch>();
  const reasonsState = useSelector(getReasonsState);
  const newReasonState = useSelector(getCreateReasonState);
  const updateReasonState = useSelector(getUpdateReasonState);
  const deleteReasonState = useSelector(getDeleteReasonState);
  const errorState = useSelector(getErrorState);

  const [openNew, setOpenNew] = useState<boolean>(false);
  const [updateReason, setUpdateReason] = useState<ReasonResponse | null>(null);
  const [isHover, setIsHover] = useState<{
    [id: string]: boolean;
  }>({});

  const fetchReasons = useCallback(async () => {
    if (id) {
      await dispatch(reasonsStateAction({ categoryId: id as string }));
    }
  }, [dispatch, id]);

  const handleSuccessToast = useCallback(
    (message: string, clearAction: any) => {
      toast(message, { type: "success" });
      setTimeout(() => {
        dispatch(clearAction());
      }, 2000);
    },
    [dispatch]
  );

  const deleteReason = async (id: string) => {
    await dispatch(deleteReasonStateAction({ id: id }));
  };

  // Memoized onMouseEnter handler
  const handleMouseEnter = useCallback((id: string) => {
    setIsHover((prevState) => ({
      ...prevState,
      [id]: true,
    }));
  }, []);

  // Memoized onMouseLeave handler
  const handleMouseLeave = useCallback((id: string) => {
    setIsHover((prevState) => ({
      ...prevState,
      [id]: false,
    }));
  }, []);

  useEffect(() => {
    fetchReasons();
    dispatch(
      setBreadcrumbState([
        { id: "summaryProperties", path: null, title: "Summary Properties" },
        { id: paths.REASONS, path: null, title: "Reasons" },
        { id: name as string, path: null, title: name as string },
      ])
    );
  }, [fetchReasons]);

  useEffect(() => {
    if (newReasonState.data && newReasonState.data.success) {
      handleSuccessToast(newReasonState.data.message, clearNewReasonState);
      fetchReasons();
    }
  }, [newReasonState, handleSuccessToast]);

  useEffect(() => {
    if (updateReasonState.data && updateReasonState.data.success) {
      handleSuccessToast(
        updateReasonState.data.message,
        clearUpdateReasonState
      );
    }
  }, [updateReasonState, handleSuccessToast]);

  useEffect(() => {
    if (deleteReasonState.data && deleteReasonState.data.success) {
      handleSuccessToast(
        deleteReasonState.data.message,
        clearDeleteReasonState
      );
    }
  }, [deleteReasonState, handleSuccessToast]);

  useEffect(() => {
    if (errorState) {
      if (errorState.redirect) {
        navigate(errorState.redirect.destination, {
          replace: errorState.redirect.permanent,
        });
        return;
      }
      toast(errorState.message, { type: "error" });
    }
  }, [errorState]);

  return (
    <PermissionGate
      categories={[adminCat.id]}
      roles={[UserRole.Admin, UserRole.Supervisor]}
    >
      <Helmet title="Reasons" />
      <div className="flex items-center justify-end mb-4">
        <Button
          label="Add Reason"
          onClick={() => setOpenNew(true)}
          exClassName="truncate"
        />
      </div>
      <Table
        style={{
          maxHeight: "calc(100% - 48px)",
        }}
        headerCells={[
          { id: "title", label: "Title", align: "text-left", width: "w-5/12" },
          {
            id: "description",
            label: "Description",
            align: "text-left",
            width: "w-7/12",
          },
          // {
          //   id: "group",
          //   label: "Group",
          //   align: "text-left",
          //   width: "w-2/12",
          // },
        ]}
        body={
          reasonsState.loading
            ? [
                <tr key="loading-row">
                  <td colSpan={2} className="h-64">
                    <div className="flex flex-col items-center justify-center">
                      <Loading />
                    </div>
                  </td>
                </tr>,
              ]
            : reasonsState.data.length > 0
            ? [
                ...reasonsState.data.map(
                  (rea: ReasonResponse, index: number) => {
                    return (
                      <tr
                        key={rea.id}
                        className="border-t border-dashed border-divider hover:bg-action-hover relative"
                        onMouseEnter={() => handleMouseEnter(rea.id)}
                        onMouseLeave={() => handleMouseLeave(rea.id)}
                      >
                        <TableBodyCell exClassName="font-medium whitespace-normal break-words">
                          <label className="cursor-pointer hover:underline">
                            {rea.title}
                          </label>
                        </TableBodyCell>
                        <TableBodyCell exClassName="whitespace-normal break-words text-justify">
                          {rea.description}
                        </TableBodyCell>
                        {/* <TableBodyCell exClassName="w-2/12 whitespace-normal break-words text-justify">
                          {rea.position?.name || "All"}
                        </TableBodyCell> */}
                        {isHover[rea.id] && (
                          <PropertyActions
                            id={rea.id}
                            onUpdate={() => setUpdateReason(rea)}
                            onDelete={() => deleteReason(rea.id)}
                          />
                        )}
                      </tr>
                    );
                  }
                ),
              ]
            : [
                <tr key="empty-row">
                  <td colSpan={2} className="h-64">
                    <div className="flex flex-col items-center justify-center">
                      <EmptyDataOverlay
                        label="No Reasons found"
                        exClassName="h-20"
                      />
                    </div>
                  </td>
                </tr>,
              ]
        }
      />
      <ToastComponent id="reasons" />
      <NewReasonDialog
        categoryId={id as string}
        open={openNew}
        setOpen={setOpenNew}
        onCompleted={fetchReasons}
      />
      <UpdateReasonDialog
        reason={updateReason}
        setReason={setUpdateReason}
        onCompleted={fetchReasons}
      />
    </PermissionGate>
  );
}
